import { useContext, useEffect, forwardRef, useImperativeHandle, useState, useMemo, useRef, useCallback } from "react";
import { AddPropertyContext } from "./AddPropertyContext";
import { ILoan as ILoanInterface, Property } from "../../../interface/tracker";
import { COUNTRY, GET_LENGTH_OPTIONS } from "../../../utils/constants";
import {
  getDateStringWithGivenFormat,
  formatDayCycle,
  getAdjustedFormattedDate,
  parseDate,
} from "../../../utils/commonUtil";
import LoanDetails from "./LoanDetails";
import { REIButton } from "../../../components/ui";
import REITab from "../../../components/ui/tab";
import { useHttpGets } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import styles from "./Loan.module.scss";
import { DATE_FORMAT } from "../../../utils/constants";

export interface ITrackerLoanValidation {
  validate: () => boolean;
}

interface ILoanProps {
  purchaseType: string;
  portfolioTrackerId?: number;
  isEditMode?: boolean;
  isExistingLoan: boolean;
  onLoanTypeChange: (value: boolean, index: number) => void;
  isUserDefault?: boolean;
}

interface ILoanHandles {
  validate: () => boolean;
}

const STR = {
  REVIEW_DATE_YEARS: 5,
  MAX_LOANS: 3,
  LOAN_HEADER: "Loan",
  TOOLTIP_ADD_LOAN_PREFIX: "Add Loan",
  LOADING: "Loading...",
  INITIALIZING: "Initializing loan...",
  ERROR_FETCH_PROFILES: "Error fetching member profiles:",
  ERROR_RATE_REVIEW_REQUIRED: "Next rate review date is required.",
  ERROR_BALANCE_GT_ZERO: "Loan balance must be greater than 0.",
  ERROR_LOAN_START_REQUIRED: "Loan start date is required.",
  ERROR_LOAN_START_FY: "Loan start date is not within the current financial year.",
  ERROR_LENGTH: "Loan length (years) must be greater than 0.",
  ERROR_INTEREST_RATE: "Interest rate must be greater than 0.",
  COUNTRY_AU: "AU",
  COUNTRY_NZ: "NZ",
  INTEREST_ONLY_FLAG: "1",
  INTEREST_ONLY_FLAG_FALSE: "0",
  LOAN_NAME_SUFFIX: " loan",
  ICON_ADD: "pi pi-plus",
  FIELD_FIXED_INTEREST_PERIOD: "FixedInterestPeriod_Years",
  PURCHASE_TYPE_NEW: "new",
  FIELD_IS_PRINCIPAL: "IsPrincipal",
  FIELD_LENGTH_YEARS: "Length_Years",
  FIELD_INTEREST_RATE: "InterestRate",
  FIELD_ESTABLISHMENT_FEES: "EstablishmentFees",
  FIELD_VALUATION_FEES: "ValuationFees",
  FIELD_OTHER_LOAN_COST: "OtherLoanCost",
};

const Loan = forwardRef<ILoanHandles, ILoanProps>((props, ref) => {
  const context = useContext(AddPropertyContext);
  const [loanErrors, setLoanErrors] = useState<
    Record<
      string,
      {
        Date?: string;
        InterestRateReviewDate?: string;
        Balance?: string;
      }
    >
  >({});
  const [loans, setLoans] = useState<ILoanInterface[]>([]);
  const [isExistingLoan, setIsExistingLoan] = useState<boolean>(props.isExistingLoan);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const tabRefs = useRef<(ITrackerLoanValidation | null)[]>([]);
  const [profileFilters, setProfileFilters] = useState();
  const [loadProfileFilters, setLoadProfileFilters] = useState<boolean>(false);
  const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const initializedFromContext = useRef(false);
  const lastSyncedRef = useRef<string | null>(null);
  const lastsyncedUserDefault = useRef<boolean | null>(null);
  const prevUserProfileRef = useRef(context?.userProfile);
  const loansRef = useRef<ILoanInterface[]>([]);

  const createDefaultLoan = useCallback(
    (index: number): ILoanInterface => {
      const now = new Date();
      const reviewDate = new Date(now);
      reviewDate.setFullYear(now.getFullYear() + STR.REVIEW_DATE_YEARS);

      return {
        TrackerLoanId: 0,
        uid: genId(),
        Name: `${formatDayCycle(index + 1)} loan`,
        RefCode: "",
        Balance: 0,
        Date: getDateStringWithGivenFormat(now, DATE_FORMAT.MMDDYYYY),
        IsPrincipal: Number(context?.userProfile?.MortgageType.PrincipalNInterest),
        Length_Years: Number(context?.userProfile?.MortgageType.LengthOfLoan),
        Length_Months: 1,
        InterestRate: Number(context?.userProfile?.MortgageType.InterestRate),
        FixedInterestPeriod_Years: 6,
        FixedInterestPeriod_Months: 1,
        InterestRateReviewDate: getAdjustedFormattedDate(new Date(now), { years: STR.REVIEW_DATE_YEARS }),
        InterestRateReviewAlert: 1,
        EstablishmentFees: Number(context?.userProfile?.LoanCost.EstPrice),
        ValuationFees: Number(context?.userProfile?.LoanCost.ValuationFee),
        OtherLoanCost: Number(context?.userProfile?.LoanCost.OtherLoanCost),
        isNewLoan: 1,
        isNameModified: false,
        isReviewDateManuallySet: false,
        isDisplayExtraFields: false,
        ModifiedFields: {},
      };
    },
    [context?.property?.ExistingLoans, props.isUserDefault, context?.userProfile]
  );

  const syncContext = (nextLoans: ILoanInterface[]) => {
    if (!context?.setProperty) return;
    context.setProperty((prev: Partial<Property>) => {
      return {
        ...prev,
        ExistingLoans: nextLoans.filter((loan) => loan.TrackerLoanId !== 0),
        Loans: nextLoans.filter((loan) => loan.TrackerLoanId === 0),
      };
    });
  };

  useEffect(() => {
    setIsExistingLoan(props.isExistingLoan);
  }, [props.isExistingLoan]);

  useEffect(() => {
    if (!context || !context.property) {
      return;
    }

    if (loadProfileFilters) return;
    const countryCode = context?.property?.CountryCode == 1 ? COUNTRY.AU : COUNTRY.NZ;

    useHttpGets(`${ApiEndPoints.tracker}/PropertyFilters/${countryCode}`)
      .then((result) => {
        if (result?.data && !result.error) {
          setProfileFilters(result.data);
          setLoadProfileFilters(true);
        }
      })
      .catch((error) => {
        console.error(STR.ERROR_FETCH_PROFILES, error);
      })
      .finally(() => {});
  }, []);

  useEffect(() => {
    if (
      context &&
      !props.portfolioTrackerId &&
      (!context.property.Loans || context.property.Loans.length === 0) &&
      (!context.property.ExistingLoans || context.property.ExistingLoans.length === 0)
    ) {
      const newLoan: ILoanInterface = createDefaultLoan(0);
      const next = [newLoan];
      loansRef.current = next;
      setLoans(next);
      syncContext(next);
    }
  }, []);

  useEffect(() => {
    if (!context || !context.property) return;
    if (props.isEditMode) return;
    let updateLoanBalance = 0;
    if (!context.property.PortfolioTrackerId && context.property.Loans && context.property.Loans.length > 0) {
      updateLoanBalance = context.property.Loans?.[0]?.Balance ?? 0;
    } else if (context.property.ExistingLoans && context.property.ExistingLoans.length > 0) {
      updateLoanBalance = context.property.ExistingLoans?.[0]?.Balance ?? 0;
    }

    setLoans((prev) => {
      const updated = prev.map((l, i) => (i === 0 ? { ...l, Balance: updateLoanBalance } : l));
      return updated;
    });
  }, [context?.property?.Loans[0], context?.property?.ExistingLoans[0]]);

  useEffect(() => {
    if (!context || !context.property) return;
    if (initializedFromContext.current) return;

    const allLoans = [...(context.property.ExistingLoans || []), ...(context.property.Loans || [])].map((l) => {
      let dateValue = l.Date ? new Date(l.Date) : new Date();
      let reviewDateValue = l.InterestRateReviewDate ? new Date(l.InterestRateReviewDate) : new Date();

      if (isNaN(dateValue.getTime())) {
        dateValue = new Date();
      }
      if (isNaN(reviewDateValue.getTime())) {
        reviewDateValue = new Date();
      }

      const fixedInterestYears = Number(l.FixedInterestPeriod_Years - 1 || 0);
      reviewDateValue.setFullYear(dateValue.getFullYear() + fixedInterestYears);

      return {
        ...l,
        Date: getDateStringWithGivenFormat(dateValue, DATE_FORMAT.MMDDYYYY),
        InterestRateReviewDate: getDateStringWithGivenFormat(reviewDateValue, DATE_FORMAT.MMDDYYYY),
        uid: l.uid || genId(),
        isReviewDateManuallySet: l.isReviewDateManuallySet ?? false,
      };
    });
    if (allLoans.length > 0) {
      const src = allLoans.map((l) =>
        l.uid
          ? l
          : {
              ...l,
              uid: genId(),
              isReviewDateManuallySet: false,
            }
      );
      loansRef.current = src;
      setLoans(src);
      initializedFromContext.current = true;
    }
  }, [context?.property]);

  useEffect(() => {
    if (activeIndex >= loans.length && loans.length > 0) {
      setActiveIndex(loans.length - 1);
    }
  }, [loans.length, activeIndex]);

  useEffect(() => {
    if (!context || !context.property) {
      return;
    }
    if (initializedFromContext.current) return;
    try {
      const str = JSON.stringify(loans || []);
      if (lastSyncedRef.current === str) return;
      syncContext(loans);
    } catch (e) {
      syncContext(loans);
      initializedFromContext.current = true;
    }
  }, [loans]);

  useEffect(() => {
    const isUserProfileTrigger = prevUserProfileRef.current !== context?.userProfile;
    if (isUserProfileTrigger) {
      upadateDataWithUserProfile();
      return;
    } else {
      if (!context?.userProfile || props.isEditMode) return;
      const isDefaultData = Number(context.userProfile.UserDetailDefault.HasDefault) === 1;

      if (lastsyncedUserDefault.current === props.isUserDefault) return;
      if (props.isUserDefault != isDefaultData) return;
      upadateDataWithUserProfile();
      lastsyncedUserDefault.current = isDefaultData ?? null;
      prevUserProfileRef.current = context?.userProfile;
    }
  }, [props.isUserDefault, context?.userProfile]);

  const upadateDataWithUserProfile = () => {
    const mortgage = context?.userProfile.MortgageType || {};
    const loanCost = context?.userProfile.LoanCost || {};
    const next = loansRef.current.map((l) => {
      const mf = l.ModifiedFields || {};
      return {
        ...l,
        IsPrincipal: mf[STR.FIELD_IS_PRINCIPAL] ? l.IsPrincipal : Number(mortgage.PrincipalNInterest),
        Length_Years: mf[STR.FIELD_LENGTH_YEARS] ? l.Length_Years : Number(mortgage.LengthOfLoan),
        InterestRate: mf[STR.FIELD_INTEREST_RATE] ? l.InterestRate : Number(mortgage.InterestRate),
        EstablishmentFees: mf[STR.FIELD_ESTABLISHMENT_FEES] ? l.EstablishmentFees : Number(loanCost.EstPrice),
        ValuationFees: mf[STR.FIELD_VALUATION_FEES] ? l.ValuationFees : Number(loanCost.ValuationFee),
        OtherLoanCost: mf[STR.FIELD_OTHER_LOAN_COST] ? l.OtherLoanCost : Number(loanCost.OtherLoanCost),
      };
    });
    loansRef.current = next;
    setLoans(next);
    syncContext(next);
  };

  useImperativeHandle(
    ref,
    () => ({
      validate: () => {
        const newErrors: Record<string, any> = {};
        let isValid = true;

        loans.forEach((loan) => {
          const loanError: {
            Date?: string;
            InterestRateReviewDate?: string;
            Balance?: string;
            InterestRate?: string;
            Length: string;
          } = {};

          if (!loan.InterestRateReviewDate) {
            loanError.InterestRateReviewDate = STR.ERROR_RATE_REVIEW_REQUIRED;
            isValid = false;
          }

          if ((loan.Balance || 0) <= 0) {
            loanError.Balance = STR.ERROR_BALANCE_GT_ZERO;
            isValid = false;
          }

          if (!loan.Date || loan.Date === "") {
            loanError.Date = STR.ERROR_LOAN_START_REQUIRED;
            isValid = false;
          } else if (!validateLoanDate(loan.Date)) {
            loanError.Date = STR.ERROR_LOAN_START_FY;
            isValid = false;
          }
          if ((loan.Length_Years || 0) <= 1) {
            loanError.Length = STR.ERROR_LENGTH;
            isValid = false;
          }

          if ((loan.InterestRate || 0) <= 0) {
            loanError.InterestRate = STR.ERROR_INTEREST_RATE;
            isValid = false;
          }

          if (Object.keys(loanError).length > 0 && loan.uid) {
            newErrors[loan.uid] = loanError;
          }
        });

        setLoanErrors(newErrors);
        return isValid;
      },
    }),
    [loans, activeIndex, profileFilters]
  );

  const validateLoanDate = (loanStartDate: string) => {
    const currentFinancialYear = (profileFilters as any)?.CurrentFiscalYear?.find(
      (x: any) => x.CountryCode == (context?.property?.CountryCode == 1 ? COUNTRY.AU : COUNTRY.NZ)
    );

    if (currentFinancialYear) {
      const financialYear = {
        StartDate: currentFinancialYear.StartDate,
        EndDate: currentFinancialYear.EndDate,
      };

      const loanStart = new Date(loanStartDate);

      const parsedStartDate = parseDate(financialYear.StartDate);
      const startDate = financialYear.StartDate && parsedStartDate ? new Date(parsedStartDate) : null;
      const parsedEndDate = parseDate(financialYear.EndDate);
      const endDate = financialYear.EndDate && parsedEndDate ? new Date(parsedEndDate) : null;

      if (!(loanStart && startDate && endDate && loanStart >= startDate && loanStart <= endDate)) {
        return false;
      } else return true;
    }
  };

  const updateLoanNames = useCallback((loansList: ILoanInterface[]): ILoanInterface[] => {
    return loansList.map((loan, index) => {
      if (!loan.isNameModified && loan.isNameModified !== undefined) {
        return {
          ...loan,
          Name: `${formatDayCycle(index + 1)} loan`,
        };
      }
      return loan;
    });
  }, []);

  const handleTabRemove = (e: any) => {
    const indexToRemove = e.index;

    const updatedLoans = loans.filter((_, i) => i !== indexToRemove);
    const renamedLoans = updateLoanNames(updatedLoans);
    const updatedRefs = tabRefs.current.filter((_, i) => i !== indexToRemove);

    let newActiveIndex = activeIndex;
    if (renamedLoans.length === 0) {
      newActiveIndex = 0;
    } else if (activeIndex === indexToRemove) {
      newActiveIndex = Math.min(indexToRemove, renamedLoans.length - 1);
    } else if (activeIndex > indexToRemove) {
      newActiveIndex = activeIndex - 1;
    }

    tabRefs.current = updatedRefs;
    setLoans(() => renamedLoans);
    syncContext(renamedLoans);
    setActiveIndex(newActiveIndex);
  };

  const handleTabAdd = () => {
    if (loans.length >= 3) return;
    const newLoan = createDefaultLoan(loans.length);
    const next = [...loans, newLoan];

    setLoans(next);
    syncContext(next);
    setActiveIndex(next.length - 1);
  };

  const makeHandleInputChange = (rowIndex: number) => (field: keyof ILoanInterface, value: any) => {
    const next = loans.map((l, i) =>
      i === rowIndex
        ? {
            ...l,
            [field]: value,
            isNameModified: field === "Name" ? true : l.isNameModified,
            ModifiedFields: { ...(l.ModifiedFields || {}), [field]: true },
          }
        : l
    );
    if (
      field === STR.FIELD_FIXED_INTEREST_PERIOD &&
      value &&
      !isNaN(Number(value)) &&
      !next[rowIndex].isReviewDateManuallySet
    ) {
      const loan = next[rowIndex];
      const startDate = loan.Date ? new Date(loan.Date) : new Date();
      if (!isNaN(startDate.getTime())) {
        const years = Number(value);
        const reviewDate = getAdjustedFormattedDate(startDate, { years: years - 1 });
        next[rowIndex] = {
          ...loan,
          InterestRateReviewDate: reviewDate,
          isReviewDateManuallySet: false,
        };
      }
    }
    setLoans(() => next);
    syncContext(next);
    const loanId = next[rowIndex].uid;
    if (field === "Balance" && loanId) {
      setLoanErrors((prev) => {
        const updated = { ...prev };
        if (value > 0) {
          if (updated[loanId]) {
            const { Balance, ...rest } = updated[loanId];
            if (Object.keys(rest).length === 0) {
              delete updated[loanId];
            } else {
              updated[loanId] = rest;
            }
          }
        } else {
          updated[loanId] = {
            ...(updated[loanId] || {}),
            Balance: STR.ERROR_BALANCE_GT_ZERO,
          };
        }
        return updated;
      });
    }
  };

  const makeHandleLoanStartDateChange = (rowIndex: number, loanObj: ILoanInterface) => (date: Date | null) => {
    if (!date || isNaN(new Date(date).getTime())) {
      return;
    }
    const FixedIntrestYear = Number(loanObj.FixedInterestPeriod_Years - 1) || 0;
    const selectedDate =
      getDateStringWithGivenFormat(date, DATE_FORMAT.MMDDYYYY) ||
      getDateStringWithGivenFormat(new Date(), DATE_FORMAT.MMDDYYYY);
    const reviewDateObj = getAdjustedFormattedDate(new Date(selectedDate), { years: FixedIntrestYear });
    const reviewDate =
      (props.isEditMode && loans[rowIndex].isNewLoan) || (!props.isEditMode && loanObj.isReviewDateManuallySet)
        ? loanObj.InterestRateReviewDate
        : reviewDateObj;

    const next = loans.map((l, i) =>
      i === rowIndex
        ? {
            ...l,
            Date: selectedDate,
            InterestRateReviewDate: reviewDate,
          }
        : l
    );

    setLoans(next);
    syncContext(next);

    setLoanErrors((prev) => {
      const loanId = loanObj.uid || "";
      const prevLoanErrors = prev[loanId] || {};
      return {
        ...prev,
        [loanId]: {
          ...prevLoanErrors,
          Date: selectedDate ? undefined : prevLoanErrors.Date || STR.ERROR_LOAN_START_REQUIRED,
          InterestRateReviewDate: reviewDate
            ? undefined
            : prevLoanErrors.InterestRateReviewDate || STR.ERROR_RATE_REVIEW_REQUIRED,
        },
      };
    });
  };

  const makeHandleReviewDateChange = (rowIndex: number) => (date: Date | null) => {
    if (!date || isNaN(new Date(date).getTime())) {
      return;
    }
    const dateStr = getDateStringWithGivenFormat(date || new Date(), DATE_FORMAT.MMDDYYYY);
    const next = loans.map((l, i) =>
      i === rowIndex ? { ...l, InterestRateReviewDate: dateStr, isReviewDateManuallySet: true } : l
    );

    setLoans((prev) => {
      return prev.map((l, i) =>
        i === rowIndex ? { ...l, InterestRateReviewDate: dateStr, isReviewDateManuallySet: true } : l
      );
    });

    syncContext(next);

    setLoanErrors((prev) => {
      const loanId = loans[rowIndex].uid;
      if (loanId) {
        const prevLoanErrors = prev[loanId] || {};
        if (!date || isNaN(new Date(date).getTime())) {
          return {
            ...prev,
            [loanId]: {
              ...prevLoanErrors,
              InterestRateReviewDate: STR.ERROR_RATE_REVIEW_REQUIRED,
            },
          };
        } else {
          const { InterestRateReviewDate, ...restErrors } = prevLoanErrors;
          const updatedErrors = { ...prev };
          if (Object.keys(restErrors).length === 0) {
            delete updatedErrors[loanId];
          } else {
            updatedErrors[loanId] = restErrors;
          }
          return updatedErrors;
        }
      }
      return prev;
    });
  };

  const tabs = useMemo(() => {
    return loans.map((loanDetail, index) => ({
      id: loanDetail.uid || `${STR.LOAN_NAME_SUFFIX}-${index}`,
      header: loanDetail.Name || `${formatDayCycle(index + 1)} ${STR.LOAN_NAME_SUFFIX}`,
      content: (
        <LoanDetails
          index={index}
          loan={loanDetail}
          purchaseType={props.purchaseType}
          isEditMode={props.isEditMode}
          isExistingLoan={!loanDetail.isDisplayExtraFields}
          onLoanTypeChange={props.onLoanTypeChange}
          errors={loanErrors[loanDetail.uid] || {}}
          getYearOptions={() => GET_LENGTH_OPTIONS(0, 50, undefined, 2)}
          getMonthOptions={() => GET_LENGTH_OPTIONS(0, 12, undefined, 2)}
          displayExtraFields={() => {
            if (!context || !context.property) return false;
            return loanDetail.isDisplayExtraFields || context.property.PurchaseType === STR.PURCHASE_TYPE_NEW;
          }}
          handleInputChange={makeHandleInputChange(index)}
          handleLoanStartDateChange={makeHandleLoanStartDateChange(index, loanDetail)}
          handleReviewDateChange={makeHandleReviewDateChange(index)}
        />
      ),
      closable: loans.length > 1,
    }));
  }, [loans, loanErrors, isExistingLoan, context?.property, props.purchaseType, props.isEditMode, props.isUserDefault]);

  if (!context) {
    return <div>{STR.LOADING}</div>;
  }

  if (!loans || loans.length === 0) {
    return <div>{STR.INITIALIZING}</div>;
  }

  return (
    <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white mb-4 mt-4">
      <div className="default-profile-header-container">
        <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">{STR.LOAN_HEADER}</h3>
      </div>
      {loans.length < 3 && (
        <div className="absolute top-2 right-2 z-10">
          <REIButton
            id={"apln-btn-addloan"}
            label=""
            icon="pi pi-plus"
            tooltip={`${STR.TOOLTIP_ADD_LOAN_PREFIX} (${loans.length + 1}/${STR.MAX_LOANS})`}
            classNames=""
            onClick={handleTabAdd}
          />
        </div>
      )}
      {tabs.length > 0 && (
        <REITab
          id="apln-tab-loans"
          handleTabClose={handleTabRemove}
          tabs={tabs}
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
          tabViewClassNames={styles.leftTabs}
        />
      )}
    </div>
  );
});

export default Loan;
